<template>
	<div v-if="avatarImage" class="congratulations-page">
		<div class="msg-wrapper">
			<h1 class="msg-title">{{ $t('congratulations') }}</h1>
			<h2 v-if="userData" class="heading-1 msg-subtitle">
				{{ studentName ?? `${userData.firstName}  ${userData.lastName}` }}
			</h2>
			<h3 class="heading-4">{{ $t('congratulationsMsg') }}</h3>
			<div class="avatar-icon">
				<div class="inner">
					<img :src="avatarImage" alt="" />
				</div>
			</div>
			<img
				class="logo ja-logo"
				src="@/assets/images/JA-centennial.png"
				alt="Junior Achievement Logo"
			/>
			<img
				v-if="nationalSponsor"
				class="logo"
				:src="nationalSponsor"
				alt="sponsors"
			/>
			<ja-button
				v-if="isBackToReportVisible"
				name="backToReport"
				:aria-label="$tc('backToReportLink')"
				class="transparent back-to-report"
				@click.native.once="goToStudentSummaryReport"
			>
				<svgicon name="arrow-left"></svgicon>
				{{ $tc('backToReportLink') }}
			</ja-button>
			<div class="buttons">
				<ja-button
					v-if="isPlayAgainVisible"
					name="congratulations-back"
					class="btn-back outlined large"
					:aria-label="$t('playAgain')"
					@click.native="playAgain"
				>
					<div class="text-play-again">{{ $t('playAgain') }}</div>
				</ja-button>
				<ja-button
					v-else
					name="congratulations-back"
					class="btn-back outlined large"
					:aria-label="$t('backToReport')"
					@click.native.once="goToStudentSummaryReport"
				>
					<div class="text-back">{{ $t('back') }}</div>
				</ja-button>
				<ja-button
					class="large"
					name="logout"
					:aria-label="$t('finishSimulation')"
					@click.native.once="logout"
					>{{ $t('logout') }}</ja-button
				>
			</div>
		</div>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import graphqlClient from '@/helpers/graphqlClient';
import UserData from '@/mixins/UserData';
import { Button } from '@ui';
import {
	swapChooseSimulation,
	getChooseSimulation,
	setChooseSimulation
} from '@/helpers/storageFunctions';
import StudentsService from '@/services/StudentsService';

export default {
	components: {
		jaButton: Button
	},
	mixins: [UserData],
	data() {
		return {
			logoutAttempt: false,
			isReplayOptionEnabled: false,
			studentName: null
		};
	},
	computed: {
		simulationType() {
			return this.$store.getters.simulationType;
		},
		nationalSponsor() {
			if (this.simulationType) {
				return this.$store.getters.nationalSponsor(this.simulationType);
			}
			return null;
		},
		avatarImage() {
			return this.$store.getters.avatarImg;
		},
		isBackToReportVisible() {
			return (
				this.userData &&
				this.userData.showReportsInNavigationBar &&
				this.isReplayOptionEnabled
			);
		},
		isPlayAgainVisible() {
			return (
				this.userData &&
				!this.userData.isViewAsStudentEnabled &&
				this.isReplayOptionEnabled
			);
		}
	},
	mounted() {
		this.$store.commit('setLoading', true);
		this.loadAvatarImage();
		this.loadSimulationInfo();
	},
	methods: {
		logout() {
			if (this.userData.isViewAsStudentEnabled) {
				window.close();
				return;
			}
			this.$store.commit('setLoading', true);
			this.$store.dispatch('logout');
		},
		goToStudentSummaryReport() {
			if (this.backBtnTriggered) {
				this.$router.push('/');
				return;
			}

			this.$store.commit('setLoading', true);

			this.$router.push({ name: 'studentSummary' });
		},
		backToReport() {
			if (getChooseSimulation().id) {
				swapChooseSimulation();
			}

			this.$store.commit('setLoading', true);

			this.$router.push({ name: 'simReport' });
		},
		async loadAvatarImage() {
			const response = await graphqlClient.query({
				query: gql`
					{
						students(where: "id=${this.userData.studentId}") {
							avatar {
								largeImageBase64
							},
							formStudentName
						}
					}
				`
			});

			if (response.data && response.data.students) {
				this.$store.commit(
					'setAvatarImg',
					response.data.students[0].avatar.largeImageBase64
				);
				this.studentName = response.data.students[0].formStudentName;
				this.$store.commit('setLoading', false);
			}
		},
		playAgain() {
			if (this.backBtnTriggered) {
				this.$router.push('/');
				return;
			}

			const studentData = {
				firstName: this.userData.firstName,
				lastName: this.userData.lastName,
				userId: this.userData.userId,
				email: this.userData.email,
				simulationId: this.userData.userActiveSimulationId
			};

			// reset the state because we want the data to come again from the BE.
			this.$store.dispatch('reset').then(() => {
				// set loader because it will be reset to false
				// we need it to true
				this.$store.commit('setLoading', true);

				// get constants because some of the things
				// used later on are there.
				this.$store.dispatch('getConstants');
			});

			StudentsService.addStudents(studentData).then(({ data }) => {
				const responseId = data.addManyStudent[0].id;

				// update sim data
				// Same simulationId different studentId
				setChooseSimulation({
					id: parseInt(studentData.simulationId),
					sid: responseId
				});

				// Go to root, BE will redirect to where we should go.
				this.$router.push('/').catch(e => e);
			});
		},
		async loadSimulationInfo() {
			graphqlClient
				.query({
					query: gql`
                {
                    simulations(where: "id = ${this.userData.userActiveSimulationId}") {
                        isReplayOptionEnabled
                    }
                }
                `,
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					this.isReplayOptionEnabled = !!data.simulations[0]
						.isReplayOptionEnabled;
				});
		}
	},
	beforeRouteEnter(to, from, next) {
		// copy the state because it gets reset after next.
		const popState = window.popStateDetected;
		// set back to false
		window.popStateDetected = false;

		next(vm => {
			vm.backBtnTriggered = popState;
		});
	}
};
</script>
<style lang="scss" scoped>
.congratulations-page {
	display: flex;
	justify-content: center;
	width: 100%;
	height: 100vh;
	max-height: 100vh;
	overflow-y: auto;

	&::before {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		opacity: 0.3;
		z-index: -1;
		background-image: url('../assets/images/congratulations-bg.png');
	}

	.msg-wrapper {
		display: flex;
		height: 100vh;
		max-height: 100vh;
		min-width: 768px;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin: auto;
		background-image: url('../assets/images/congratulations-bg-layer.svg');

		.msg-title {
			color: $primary-state;
			font-family: 'RobotoMedium';
			font-size: 34px;
			line-height: 51px;
			text-align: center;
			margin-bottom: 0;
			letter-spacing: 0.25;

			@media (min-width: $breakpoint-mid) {
				font-size: 48px;
				line-height: 64px;
				letter-spacing: 0;
			}
		}

		.msg-subtitle {
			color: $primary-state;
			text-align: center;
			margin: 24px 0 16px 0;
			@media (min-height: $breakpoint-min) {
				margin: 32px 0 16px 0;
			}
		}

		.heading-4 {
			color: #45575d;
			margin-top: 16px;
			margin-bottom: 0;
			font-family: 'RobotoRegular';
			font-weight: normal;
			text-align: center;
		}

		.avatar-icon {
			position: relative;
			height: 180px;
			width: 180px;
			border-radius: 100%;
			background: $avatar-wrapper-color;
			margin-top: 24px;
			flex-shrink: 0;

			@media (min-height: $breakpoint-min) {
				height: 242px;
				width: 242px;
				margin-top: 32px;
			}

			.inner {
				position: absolute;
				height: 152px;
				width: 152px;
				border-radius: 100%;
				top: 4px;
				left: 4px;
				right: 4px;
				bottom: 4px;
				margin: auto;
				background-color: $container-bgcolor;

				@media (min-height: $breakpoint-min) {
					height: 214px;
					width: 214px;
				}

				img {
					width: 90%;
					margin: 5%;
				}
			}
		}

		.logo {
			margin-top: 64px;
		}
		.ja-logo {
			height: 64px;
			width: auto;
		}

		.back-to-report {
			font-weight: bold;
			svg {
				vertical-align: baseline;
			}
		}

		button {
			margin: 24px 10px;
			@media (min-height: $breakpoint-min) {
				margin-top: 32px;
			}
		}
	}
}
</style>
